import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Building2, ChevronLeft, ChevronRight, Plus, Loader2, X } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AdminAuthContext";
import {
    Panel, PageHead, Loading, LoadError, EmptyState, PartnershipBadge, VisibilityBadge,
    PARTNERSHIP_STATUSES, PARTNERSHIP_LABELS, fmtDate,
} from "../ui";

const SELECT =
    "rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-xs text-[#F7F5EF] outline-none focus:border-[#10B981] transition-colors";
const fieldCls =
    "w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors";

const CreateHospitalDialog = ({ open, onClose, onCreated }) => {
    const [form, setForm] = useState({ legal_name: "", display_name: "", city: "", state: "", specialties: "", services: "" });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const { data } = await api.post("/admin/hospitals", {
                ...form,
                display_name: form.display_name || form.legal_name,
                specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
            });
            onCreated(data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(
                typeof detail === "string"
                    ? detail
                    : "Could not create the hospital. Please check the details and try again."
            );
            setSaving(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <button
                type="button"
                data-testid="hospital-create-backdrop"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-black/60"
            />
            <div
                data-testid="hospital-create-dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Add hospital"
                className="relative w-full max-w-lg rounded-2xl border border-[#1D322E] bg-[#12201D] p-6 shadow-2xl"
            >
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-[#F7F5EF]">Add hospital</h2>
                    <button type="button" data-testid="hospital-create-close" onClick={onClose} className="text-[#64716D] hover:text-[#F7F5EF] transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <p className="mt-1 text-xs text-[#64716D]">
                    New hospitals start as internal records — never publicly visible by default.
                </p>
                <form onSubmit={submit} className="mt-4 space-y-4">
                    <input data-testid="hospital-create-legal-name" required placeholder="Legal / entity name *" value={form.legal_name} onChange={set("legal_name")} className={fieldCls} />
                    <input data-testid="hospital-create-display-name" placeholder="Display name (optional)" value={form.display_name} onChange={set("display_name")} className={fieldCls} />
                    <div className="grid grid-cols-2 gap-3">
                        <input data-testid="hospital-create-city" required placeholder="City *" value={form.city} onChange={set("city")} className={fieldCls} />
                        <input data-testid="hospital-create-state" placeholder="State" value={form.state} onChange={set("state")} className={fieldCls} />
                    </div>
                    <input data-testid="hospital-create-specialties" placeholder="Specialties (comma separated)" value={form.specialties} onChange={set("specialties")} className={fieldCls} />
                    <textarea data-testid="hospital-create-services" rows={2} placeholder="International patient services (optional)" value={form.services} onChange={set("services")} className={fieldCls} />
                    {error && (
                        <p role="alert" data-testid="hospital-create-error" className="rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        data-testid="hospital-create-submit"
                        disabled={saving}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {saving ? "Creating…" : "Create hospital"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export const AdminHospitals = () => {
    const { user } = useAdminAuth();
    const isAdmin = user?.role === "ADMIN";
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ city: "", specialty: "", partnership_status: "", active: "" });
    const [meta, setMeta] = useState({ cities: [], specialties: [] });
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            setLoading(true);
            const params = new URLSearchParams({ page: String(page), limit: "20" });
            if (search.trim()) params.set("search", search.trim());
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            api.get(`/admin/hospitals?${params.toString()}`)
                .then((res) => {
                    setData(res.data);
                    setMeta({ cities: res.data.cities || [], specialties: res.data.specialties || [] });
                    setError("");
                })
                .catch(() => setError("Could not load hospitals. Please refresh."))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(t);
    }, [search, filters, page]);

    const setFilter = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        setPage(1);
    };

    const items = data?.items || [];

    return (
        <div data-testid="admin-hospitals-page">
            <PageHead
                title="Hospitals Registry"
                subtitle="Internal network of hospitals and their international patient services."
                actions={
                    isAdmin ? (
                        <button
                            type="button"
                            data-testid="hospital-create-open-button"
                            onClick={() => setCreateOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99]"
                        >
                            <Plus className="w-4 h-4" /> Add hospital
                        </button>
                    ) : null
                }
            />

            {!isAdmin && (
                <p className="mb-4 rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-2.5 text-xs text-[#64716D]">
                    Read-only view — hospital records are managed by UniversHeal admins.
                </p>
            )}

            <div className="flex flex-wrap items-center gap-3" data-testid="hospitals-filter-bar">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64716D]" />
                    <input
                        data-testid="hospitals-search-input"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search hospital or city…"
                        className="w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] pl-9 pr-4 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                    />
                </div>
                <select data-testid="hospitals-filter-city" value={filters.city} onChange={(e) => setFilter("city", e.target.value)} className={SELECT} aria-label="Filter by city">
                    <option value="">All cities</option>
                    {meta.cities.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
                <select data-testid="hospitals-filter-specialty" value={filters.specialty} onChange={(e) => setFilter("specialty", e.target.value)} className={SELECT} aria-label="Filter by specialty">
                    <option value="">All specialties</option>
                    {meta.specialties.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                <select data-testid="hospitals-filter-partnership" value={filters.partnership_status} onChange={(e) => setFilter("partnership_status", e.target.value)} className={SELECT} aria-label="Filter by partnership status">
                    <option value="">All partnership statuses</option>
                    {PARTNERSHIP_STATUSES.map((p) => (
                        <option key={p} value={p}>{PARTNERSHIP_LABELS[p]}</option>
                    ))}
                </select>
                <select data-testid="hospitals-filter-active" value={filters.active} onChange={(e) => setFilter("active", e.target.value)} className={SELECT} aria-label="Filter by active status">
                    <option value="">Active + inactive</option>
                    <option value="true">Active only</option>
                    <option value="false">Inactive only</option>
                </select>
            </div>

            {error ? (
                <div className="mt-6"><LoadError message={error} /></div>
            ) : loading && !data ? (
                <Loading />
            ) : (
                <Panel className="mt-6 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left" data-testid="hospitals-table">
                            <thead>
                                <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                                    {["Hospital", "City", "Specialties", "Partnership", "Visibility", "Updated", ""].map((h) => (
                                        <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((h) => (
                                    <tr
                                        key={h.id}
                                        data-testid={`hospital-row-${h.display_name || h.legal_name}`}
                                        className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors"
                                    >
                                        <td className="px-4 py-3 text-sm text-[#F7F5EF] font-medium">
                                            {h.display_name || h.legal_name}
                                            {!h.active && <span className="ml-2 text-xs text-red-400">Inactive</span>}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{h.city}{h.state ? `, ${h.state}` : ""}</td>
                                        <td className="px-4 py-3 text-xs text-[#A3B8B0]">
                                            {h.specialties?.length ? h.specialties.slice(0, 3).join(", ") + (h.specialties.length > 3 ? ` +${h.specialties.length - 3}` : "") : "—"}
                                        </td>
                                        <td className="px-4 py-3"><PartnershipBadge status={h.partnership_status} /></td>
                                        <td className="px-4 py-3"><VisibilityBadge visible={h.public_visible} /></td>
                                        <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(h.updated_at)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <Link to={`/admin/hospitals/${h.id}`} data-testid={`hospital-open-${h.id}`} className="text-xs font-semibold text-emerald-300 hover:text-emerald-200">
                                                Open →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {items.length === 0 && (
                        <EmptyState
                            icon={Building2}
                            title="No hospitals yet"
                            hint="Add the first hospital to start building your referral network. Records stay private until explicitly approved."
                        />
                    )}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#1A2E2A]" data-testid="hospitals-pagination">
                        <p className="text-xs text-[#64716D]" data-testid="hospitals-total-count">
                            {data?.total ?? 0} hospital{(data?.total ?? 0) === 1 ? "" : "s"} · page {data?.page ?? 1} of {data?.pages ?? 1}
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="hospitals-pagination-prev"
                                disabled={(data?.page ?? 1) <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-lg border border-[#1D322E] p-2 text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Previous page"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                data-testid="hospitals-pagination-next"
                                disabled={(data?.page ?? 1) >= (data?.pages ?? 1)}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-lg border border-[#1D322E] p-2 text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Next page"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </Panel>
            )}

            <CreateHospitalDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(h) => {
                    setCreateOpen(false);
                    window.location.assign(`/admin/hospitals/${h.id}`);
                }}
            />
        </div>
    );
};
